import type { ThemeConfig } from 'antd';
import { App, ConfigProvider, theme as antdTheme } from 'antd';
import enUS from 'antd/locale/en_US';
import kkKZ from 'antd/locale/kk_KZ';
import ruRU from 'antd/locale/ru_RU';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';

import { Languages } from '@/shared/constants';
import { getSystemTheme } from '@/shared/services/theme';
import { useAppDispatch, useAppSelector } from '@/store';
import { setTheme } from '@/store/app.slice';
import { getPalette } from '@/styles/palette';

/** Высота контролов и пунктов меню: плотная для мыши, 44px — минимальная область нажатия на тач-устройствах. */
const CONTROL_HEIGHT = { pointer: 36, touch: 44 };
const MENU_ITEM_HEIGHT = { pointer: 40, touch: 44 };

const COARSE_POINTER_QUERY = '(pointer: coarse)';

const subscribeCoarsePointer = (onChange: () => void): (() => void) => {
  const mq = window.matchMedia(COARSE_POINTER_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
};

const getCoarsePointer = (): boolean =>
  window.matchMedia(COARSE_POINTER_QUERY).matches;

const getAntdTheme = (
  mode: 'light' | 'dark',
  isCoarsePointer: boolean
): ThemeConfig => {
  const palette = getPalette(mode);

  return {
    algorithm:
      mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: palette.brandPrimary,
      colorLink: palette.brandPrimary,
      colorInfo: palette.statusInfo,
      colorSuccess: palette.statusSuccess,
      colorWarning: palette.statusWarning,
      colorError: palette.statusError,

      colorText: palette.textPrimary,
      colorTextSecondary: palette.textSecondary,
      colorTextTertiary: palette.textTertiary,
      colorTextQuaternary: palette.textDisabled,

      colorBgLayout: palette.background,
      colorBgContainer: palette.surface,
      colorBgElevated: mode === 'dark' ? palette.surfaceMuted : palette.surface,
      colorFillAlter: palette.surfaceMuted,
      colorBorder: palette.borderStrong,
      colorBorderSecondary: palette.border,

      fontFamily: 'Manrope, -apple-system, system-ui, sans-serif',
      borderRadius: 6,
      controlHeight: isCoarsePointer
        ? CONTROL_HEIGHT.touch
        : CONTROL_HEIGHT.pointer
    },
    components: {
      // Отступы вокруг заголовков задаёт раскладка (gap), а не сам заголовок.
      Typography: {
        titleMarginTop: 0,
        titleMarginBottom: 0
      },
      Menu: {
        itemHeight: isCoarsePointer
          ? MENU_ITEM_HEIGHT.touch
          : MENU_ITEM_HEIGHT.pointer,
        darkItemBg: palette.siderBg,
        darkSubMenuItemBg: palette.siderBg,
        darkPopupBg: palette.siderBg,
        darkItemColor: palette.siderText,
        darkItemHoverColor: palette.siderTextStrong,
        darkItemHoverBg: palette.siderHover,
        darkItemSelectedBg: palette.brandPrimary,
        darkItemSelectedColor: palette.brandOnPrimary,
        darkGroupTitleColor: palette.siderTextMuted
      }
    }
  };
};

interface IProps {
  children: React.ReactNode;
}

const ThemeProvider: React.FC<IProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useAppDispatch();
  const { theme, themeMode } = useAppSelector((state) => state.app);
  const isCoarsePointer = useSyncExternalStore(
    subscribeCoarsePointer,
    getCoarsePointer
  );
  const antdThemeConfig = useMemo(
    () => getAntdTheme(theme, isCoarsePointer),
    [theme, isCoarsePointer]
  );

  // Включаем CSS-переменные тёмной схемы (html[data-theme='dark']) из src/styles/theme.scss.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Снимаем инлайн-фон от анти-моргального скрипта (public/index.html) — дальше фон ведёт CSS.
    document.documentElement.style.backgroundColor = '';
  }, [theme]);

  // В режиме system следим за системной темой и обновляем эффективную схему.
  useEffect(() => {
    if (themeMode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => dispatch(setTheme(getSystemTheme()));
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [themeMode, dispatch]);

  const locale = useMemo(() => {
    switch (currentLang) {
      case Languages.ru:
        return ruRU;
      case Languages.en:
        return enUS;
      case Languages.kk:
        return kkKZ;
      default:
        return ruRU;
    }
  }, [currentLang]);

  return (
    <ConfigProvider locale={locale} theme={antdThemeConfig}>
      {/* Уведомления — ниже липкой шапки каркаса (72px), чтобы не закрывать её действия. */}
      <App component={false} notification={{ top: 88 }}>
        {children}
      </App>
    </ConfigProvider>
  );
};

export default ThemeProvider;
